import AsyncReactSelect from "react-select/async";
import Option from "./components/Option";
import DropdownIndicator from "./components/DropdownIndicator";
import ValueContainer from "./components/ValueContainer";
import MultiValue from "./components/MultiValue";
import MenuList from "./components/MenuList";
import { GroupBase, OptionsOrGroups, SelectInstance } from "react-select";
import { SelectItem, SelectNewOption } from "./Select";
import { classNames } from "./styles";
import { useCustomTranslation } from "../../helpers/translateHelpers";
import React, { forwardRef, useEffect } from "react";
import { useDebouncedCallback } from "use-debounce";
import { DEBOUNCE_MS } from "../../helpers/debounce";

const ariaLiveMessages = {
  guidance: () => "",
  onChange: () => "",
  onFilter: () => "",
  onFocus: () => "",
};

interface AsyncSelectProps {
  invalid?: boolean;
  loadOptions: (
    inputValue: string,
    callback: (
      options: OptionsOrGroups<SelectItem, GroupBase<SelectItem>>,
    ) => void,
  ) => void;
  value: SelectItem | SelectItem[] | null;
  onChange: (value: SelectItem | SelectItem[] | null) => void;
  isMulti?: boolean;
  placeholder?: string;
  newOption?: SelectNewOption; // Using when we want to allow user tu create new option
  multivalueTagLabelKey?: string; // Translate key to show on tag when using multivalue
  menuListFooter?: React.ReactNode;
  noOptionsText?: string;
  isClearable?: boolean;
  enableDefaultOptions?: boolean;
  isDisabled?: boolean;
  instanceId?: string;
}

// Debounce the loadOptions callback to reduce requests while typing.
// Bypass debounce for empty input so defaultOptions are shown immediately.
type LoadOptionsCallback = (
  options: OptionsOrGroups<SelectItem, GroupBase<SelectItem>>,
) => void;

const AsyncSelect = forwardRef<
  SelectInstance<SelectItem, false, GroupBase<SelectItem>>,
  AsyncSelectProps
>(
  (
    {
      loadOptions,
      invalid,
      newOption,
      onChange,
      isMulti,
      value,
      placeholder,
      menuListFooter,
      noOptionsText,
      multivalueTagLabelKey,
      isClearable = false,
      enableDefaultOptions,
      isDisabled,
      instanceId,
    },
    ref,
  ) => {
    const { tInput } = useCustomTranslation();

    const debouncedLoad = useDebouncedCallback(
      (inputValue: string, callback: LoadOptionsCallback) => {
        loadOptions(inputValue, callback);
      },
      DEBOUNCE_MS,
    );

    // cancel pending debounced calls on unmount
    useEffect(() => {
      return () => {
        debouncedLoad?.cancel?.();
      };
    }, [debouncedLoad]);

    return (
      <AsyncReactSelect
        cacheOptions
        loadOptions={debouncedLoad}
        defaultOptions={enableDefaultOptions ?? true} // or pass an array if you have default options
        components={{
          Option: Option,
          DropdownIndicator: DropdownIndicator,
          ValueContainer: ValueContainer,
          MultiValue: MultiValue,
          MenuList: MenuList,
        }}
        classNames={classNames(invalid)}
        /* @ts-expect-error Bug in the react-select, based on documentation isMulti is boolean, but types are only false and undefined*/
        isMulti={isMulti}
        closeMenuOnSelect={!isMulti}
        hideSelectedOptions={false}
        onChange={onChange}
        value={value}
        ariaLiveMessages={ariaLiveMessages}
        screenReaderStatus={() => ""}
        placeholder={placeholder}
        noOptionsMessage={() => noOptionsText ?? tInput("select_no_options")}
        loadingMessage={() => tInput("select_loading")}
        ref={ref}
        isClearable={isClearable}
        isDisabled={isDisabled}
        instanceId={instanceId}
        {...{ newOption, menuListFooter, multivalueTagLabelKey }}
      />
    );
  },
);

export default AsyncSelect;
